import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLang } from "@/context/LanguageContext";
import { loc, catLabel } from "@/lib/localize";
import { PageHero } from "@/components/common/PageHero";
import { ArticleCard } from "@/components/common/ArticleCard";
import { Newsletter } from "@/components/common/Newsletter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search as SearchIcon } from "lucide-react";

const CATS = ["Latest", "Market News", "Technical Analysis", "Fundamental Analysis", "Forex Analysis", "Gold Analysis", "Weekly Outlook"];

export default function MarketAnalysis() {
  const { L, isAr } = useLang();
  const P = L.pages.analysis;
  usePageSeo("analysis");
  const [cat, setCat] = useState("Latest");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const { data } = useQuery({
    queryKey: ["analysis", cat, q, page],
    queryFn: async () => (await api.get(`/articles`, { params: { category: cat, search: q || undefined, page, page_size: 9 } })).data,
  });
  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / 9));
  const featured = page === 1 && !q ? items[0] : null;
  const rest = featured ? items.slice(1) : items;

  return (
    <>
      <PageHero
        overline={P.overline}
        title={P.title}
        description={P.desc}
        breadcrumbs={[{ label: L.common.home, to: "/" }, { label: P.overline }]}
      />

      <section className="py-16 lg:py-24">
        <div className="container-tight">
          <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {CATS.map((c) => (
                <button
                  key={c}
                  onClick={() => { setCat(c); setPage(1); }}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${cat === c ? "bg-navy text-white" : "border border-hair bg-white text-subtle hover:text-finblue"}`}
                  data-testid={`analysis-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {catLabel(c, isAr)}
                </button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); setQ(search); setPage(1); }} className="flex gap-2">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={P.searchPlaceholder} className="pl-9" data-testid="analysis-search" />
              </div>
              <Button type="submit" variant="outline">Search</Button>
            </form>
          </div>

          {featured && (
            <Link to={`/market-analysis/${featured.slug}`} className="mb-10 grid overflow-hidden rounded-2xl border border-hair bg-white shadow-sm transition-shadow hover:shadow-md lg:grid-cols-2" data-testid="featured-article">
              <div className="h-64 overflow-hidden bg-canvas lg:h-auto">
                <img src={featured.featured_image || "https://images.unsplash.com/photo-1651341050677-24dba59ce0fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"} alt={loc(featured, "title", isAr)} className="h-full w-full object-cover" />
              </div>
              <div className="flex flex-col justify-center p-8">
                <span className="overline">{catLabel(featured.category, isAr)} · {isAr ? "مميز" : "Featured"}</span>
                <h2 className="mt-3 font-heading text-2xl font-semibold text-ink lg:text-3xl">{loc(featured, "title", isAr)}</h2>
                <p className="mt-3 text-subtle">{loc(featured, "excerpt", isAr)}</p>
                <span className="mt-4 text-sm font-semibold text-finblue">{L.common.readArticle} →</span>
              </div>
            </Link>
          )}

          {rest.length ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-hair bg-canvas py-16 text-center text-subtle">{isAr ? "لا توجد مقالات." : "No articles found."}</div>
          )}

          {pages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="text-sm text-subtle">Page {page} of {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          )}

          <div className="mt-16"><Newsletter variant="light" /></div>
        </div>
      </section>
    </>
  );
}
